// CloneArtifactRunDialog — one-click "Clone to new agent run" flow.
//
// Opens a small modal pre-filled with an artifact's original goal,
// model, and tools. The user can edit the goal (the most common
// thing to tweak between runs) then hit "Run agent" — we navigate
// to /agents/builder with the deep-link params + `autorun=1` so the
// builder hydrates the draft and starts the run automatically.
//
// Shape of the artifact payload mirrors `ArtifactPayload` in
// `src/pages/LibraryArtifact.tsx`. We accept it loosely typed so any
// surface holding a Library entry can call us without re-importing the
// type.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Play, Wrench } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { modelLabel } from "@/lib/llmModels";

export interface CloneArtifactPayload {
  goal?: string;
  brain_model?: string;
  tools?: string[];
  agent_name?: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payload: CloneArtifactPayload;
  /** Used as the agent name fallback so the run is easy to find later. */
  artifactTitle?: string;
  /** When set, the regenerate flow will append a new version to this
   *  library entry instead of inserting a brand-new artifact. The agent
   *  builder reads it from the URL and threads it into the save handler. */
  replaceEntryId?: string;
}

const MAX_GOAL = 2000;

export default function CloneArtifactRunDialog({
  open,
  onOpenChange,
  payload,
  artifactTitle,
  replaceEntryId,
}: Props) {
  const navigate = useNavigate();
  const [goal, setGoal] = useState(payload.goal ?? "");

  // Reset the goal whenever the dialog opens against a (potentially
  // different) artifact, so a second open doesn't stick to the user's
  // last edit on a different artifact.
  useEffect(() => {
    if (open) setGoal(payload.goal ?? "");
  }, [open, payload.goal]);

  const tools = payload.tools ?? [];
  const trimmed = goal.trim();
  const tooLong = trimmed.length > MAX_GOAL;
  const canRun = trimmed.length > 0 && !tooLong;

  const onRun = () => {
    if (!canRun) return;
    const params = new URLSearchParams();
    params.set("goal", trimmed);
    if (payload.brain_model) params.set("model", payload.brain_model);
    if (tools.length) params.set("tools", tools.join(","));
    const name = payload.agent_name || artifactTitle;
    if (name) params.set("name", name.slice(0, 80));
    params.set("autorun", "1");
    // Tell the builder this run should replace the original artifact's
    // payload (auto-snapshotting the prior version) instead of creating
    // a new library entry on save.
    if (replaceEntryId) params.set("replace_entry_id", replaceEntryId);
    onOpenChange(false);
    navigate(`/agents/builder?${params.toString()}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bot className="w-4 h-4 text-primary" /> Clone to new agent run
          </DialogTitle>
          <DialogDescription className="text-xs">
            Reuses this artifact's model and tools. Tweak the goal, then run.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label htmlFor="clone-goal" className="text-xs font-mono">
              Goal
            </Label>
            <Textarea
              id="clone-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              rows={5}
              className="mt-1 font-mono text-sm"
              placeholder="What should the agent do this time?"
              autoFocus
              aria-invalid={tooLong || undefined}
            />
            <div className="mt-1 flex items-center justify-between text-[10px] font-mono">
              <span
                className={
                  tooLong ? "text-destructive" : "text-muted-foreground"
                }
              >
                {trimmed.length}/{MAX_GOAL}
              </span>
              {!trimmed && (
                <span className="text-muted-foreground">
                  Goal is required.
                </span>
              )}
            </div>
          </div>

          <div className="rounded-md border border-border bg-muted/30 p-2.5 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              Locked from this artifact
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono">
              <Badge variant="outline" className="gap-1">
                <Bot className="w-3 h-3" />
                {payload.brain_model
                  ? modelLabel(payload.brain_model)
                  : "Default model"}
              </Badge>
              {tools.length > 0 ? (
                <>
                  <Badge variant="outline" className="gap-1">
                    <Wrench className="w-3 h-3" />
                    {tools.length} tool{tools.length === 1 ? "" : "s"}
                  </Badge>
                  {tools.slice(0, 4).map((t) => (
                    <Badge key={t} variant="secondary" className="text-[10px]">
                      {t}
                    </Badge>
                  ))}
                  {tools.length > 4 && (
                    <Badge variant="secondary" className="text-[10px]">
                      +{tools.length - 4}
                    </Badge>
                  )}
                </>
              ) : (
                <span className="text-muted-foreground">No tools</span>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              You can change the model and tools on the next screen before the
              run starts.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            type="button"
          >
            Cancel
          </Button>
          <Button onClick={onRun} disabled={!canRun} type="button">
            <Play className="w-4 h-4 mr-1.5" /> Run agent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}